import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import { CheckCircle, Payment } from '@mui/icons-material';
import { collection, doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';

const defaultPlans = [
  { id: 'mensal', label: 'Mensal', pricePerMonth: 20, cycleMonths: 1, highlight: false },
  { id: 'trimestral', label: 'Trimestral', pricePerMonth: 18, cycleMonths: 3, highlight: false, subtitle: 'Economize 10%' },
  { id: 'semestral', label: 'Semestral', pricePerMonth: 16, cycleMonths: 6, highlight: true, subtitle: 'Melhor custo/benefício' },
  { id: 'anual', label: 'Anual', pricePerMonth: 14, cycleMonths: 12, highlight: false, subtitle: 'Pague 12, leve 14 meses' }
];

function formatBRL(value) {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function BillingDialog({ open, onClose, user }) {
  const [plans, setPlans] = useState(defaultPlans);
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(null);
  const [error, setError] = useState('');

  const totalText = (plan) => formatBRL(plan.pricePerMonth * plan.cycleMonths);

  useEffect(() => {
    if (!open) return;
    const fetchConfig = async () => {
      setLoading(true);
      try {
        const snap = await getDoc(doc(collection(db, 'billing_config'), 'general'));
        if (snap.exists()) {
          const data = snap.data();
          const loaded = Object.entries(data.plans || {}).map(([id, value]) => ({
            id,
            label: value.label || id,
            pricePerMonth: Number(value.pricePerMonth || value.price || 0),
            cycleMonths: Number(value.cycleMonths || value.months || 1),
            highlight: Boolean(value.highlight),
            subtitle: value.subtitle || ''
          }));
          if (loaded.length) setPlans(loaded);
        }
      } catch (e) {
        console.warn('Não foi possível carregar config de planos, usando padrão.', e);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, [open]);

  const currentDue = useMemo(() => {
    if (!user?.expiraEm) return null;
    const d = new Date(user.expiraEm);
    return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('pt-BR');
  }, [user?.expiraEm]);

  const handlePay = async (plan) => {
    setError('');
    setPaying(plan.id);
    try {
      const createPref = httpsCallable(functions, 'createMercadoPagoPreference');
      const resp = await createPref({
        planId: plan.id,
        planName: plan.label,
        pricePerMonth: plan.pricePerMonth,
        cycleMonths: plan.cycleMonths,
        customerEmail: user?.email
      });
      const initPoint = resp?.data?.init_point;
      if (!initPoint) throw new Error('Falha ao criar pagamento.');
      window.location.href = initPoint;
    } catch (e) {
      console.error('Erro ao iniciar pagamento:', e);
      setError(e.message || 'Erro ao iniciar pagamento. Tente novamente.');
      setPaying(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Typography variant="h6" fontWeight="bold">Escolha seu plano</Typography>
          {currentDue && (
            <Chip label={`Seu acesso atual vence em ${currentDue}`} color="warning" size="small" />
          )}
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Pague por Pix ou Cartão via Mercado Pago. Parcelas no cartão seguem juros do emissor.
        </Typography>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        )}
        <Grid container spacing={2}>
          {plans.map((plan) => (
            <Grid item xs={12} sm={6} md={3} key={plan.id}>
              <Paper
                elevation={plan.highlight ? 6 : 2}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: plan.highlight ? '2px solid #1a237e' : '1px solid #e0e0e0',
                  height: '100%'
                }}
              >
                <Stack spacing={1.2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography fontWeight="bold">{plan.label}</Typography>
                    {plan.highlight && <Chip size="small" color="primary" label="Recomendado" />}
                  </Stack>
                  {plan.subtitle && (
                    <Typography variant="body2" color="textSecondary">{plan.subtitle}</Typography>
                  )}
                  <Typography variant="h5" fontWeight="bold" color="primary">
                    {formatBRL(plan.pricePerMonth)} <Typography component="span" variant="caption">/mês</Typography>
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total do ciclo ({plan.cycleMonths}x): {totalText(plan)}
                  </Typography>
                  <Button
                    variant={plan.highlight ? 'contained' : 'outlined'}
                    startIcon={paying === plan.id ? <CircularProgress size={18} /> : <Payment />}
                    disabled={paying !== null && paying !== plan.id}
                    onClick={() => handlePay(plan)}
                  >
                    {paying === plan.id ? 'Abrindo checkout...' : 'Pagar com Mercado Pago'}
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
        <Box sx={{ mt: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CheckCircle color="success" />
            <Box>
              <Typography fontWeight="bold">Acesso liberado automaticamente</Typography>
              <Typography variant="body2" color="textSecondary">
                Assim que o pagamento for confirmado, seu acesso é renovado pelo período escolhido.
              </Typography>
            </Box>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
